import { css } from '@emotion/react'
import { Card, colors, CompanyLogo } from 'plume-ui'
import { OverseasCompanyData } from '../../../../types/api'
import { CardInnerTitle } from '../../../atoms/CardInnerTitle'
import {
  Table,
  TableCell,
  TableHead,
  TableHeading,
  TableRow,
} from '../../../atoms/Table'

import Link from 'next/link'
import { A } from '../../../atoms/A'
import { COMPANY_HREF } from '@/lib/url'
import { toUnitNumber } from '@/lib/number'
import { Pagination } from '../../../common/Pagination'
import { COMPANIES_PER_PAGE } from '../../../../pages/investors/[shortname]'
import { LineClampText } from '../../../atoms/LineClampText'
import { formatLastFundingType } from '@/lib/roundtype'
import { Help } from '../../../common/Help'
import { EmptyCard } from '../../../common/EmptyCard'
import { FormattedDate } from '../../../atoms/FormattedDate'

type Props = {
  companies: OverseasCompanyData[] | undefined
  loading: boolean
  page: number
  count: number
  handleChangePage: (page: number) => void
}

export const OverseasInvestmentTable: React.FC<Props> = ({
  companies,
  page,
  count,
  handleChangePage,
}) => {
  return (
    <Card className="overseas-invested-card">
      <CardInnerTitle
        className="overseas-invested-table-title"
        title="スタートアップ"
      />
      {companies?.length ? (
        <>
          <Table className="overseas-invested-table">
            <TableHead className="header">
              <tr
                css={css`
                  > th {
                    background-color: ${colors.white10};
                  }
                `}
              >
                <TableHeading>企業名</TableHeading>
                <TableHeading align="center">
                  <div>
                    <div>最新ラウンド</div>
                    <div>調達日</div>
                  </div>
                </TableHeading>
                <TableHeading align="center" className="last-funding-type">
                  <div>
                    <div>最新ラウンド</div>
                    <div>タイプ</div>
                  </div>
                  <Help
                    className="help"
                    linkTo="https://help.initial.inc/ja/articles/5489432"
                    css={css`
                      vertical-align: bottom;
                    `}
                  >
                    定義を見る
                  </Help>
                </TableHeading>
                <TableHeading>総調達額</TableHeading>
                <TableHeading>調達後評価額</TableHeading>
              </tr>
            </TableHead>
            <tbody>
              {companies.map((company, i) => {
                return (
                  <TableRow highlightOnHover key={i} className="company-row">
                    <TableCell>
                      <div
                        css={css`
                          display: flex;
                        `}
                      >
                        <div
                          className="company-logo"
                          css={css`
                            margin-right: 8px;
                          `}
                        >
                          <Link
                            as={`/companies/${company.shortname}`}
                            href={COMPANY_HREF}
                            passHref
                          >
                            <a>
                              <CompanyLogo
                                src={
                                  company.logoUrl ??
                                  '/static/images/default/company.png'
                                }
                                size="normal"
                                name={company.name}
                              />
                            </a>
                          </Link>
                        </div>
                        <div>
                          <div className="company-name">
                            <Link
                              as={`/companies/${company.shortname}`}
                              href={COMPANY_HREF}
                              passHref
                            >
                              <A decoration="none">{company.name}</A>
                            </Link>
                          </div>
                          <div
                            className="company-description"
                            css={css`
                              margin-top: 8px;
                            `}
                          >
                            <LineClampText
                              justify
                              lineNumber={5}
                              css={css`
                                font-size: 12px;
                                line-height: 1.5;
                              `}
                              stripLineBreaks
                            >
                              {company.description}
                            </LineClampText>
                          </div>
                        </div>
                      </div>
                    </TableCell>

                    <TableCell
                      className="last-funding-date"
                      width="104px"
                      css={css`
                        vertical-align: middle;
                      `}
                    >
                      <FormattedDate>
                        {company.lastFundingType?.date}
                      </FormattedDate>
                    </TableCell>
                    <TableCell
                      className="round-type"
                      width="128px"
                      css={css`
                        padding-left: 23px;
                        vertical-align: middle;
                      `}
                    >
                      <div>
                        {company.lastFundingType?.type &&
                          formatLastFundingType(company.lastFundingType?.type)}
                      </div>
                    </TableCell>
                    <TableCell
                      className="total-fundraise"
                      width="104px"
                      css={css`
                        vertical-align: middle;
                      `}
                    >
                      {company?.totalFundraise?.currency &&
                        company?.totalFundraise?.value && (
                          <div
                            css={css`
                              display: flex;
                              justify-content: end;
                            `}
                          >
                            <div
                              className="currency"
                              css={css`
                                margin-right: 2px;
                              `}
                            >
                              {company?.totalFundraise?.currency}
                            </div>
                            <div className="value">
                              {toUnitNumber(
                                company.totalFundraise.value
                              ).value.toFixed(1)}
                            </div>
                            <div className="unit">
                              {toUnitNumber(company.totalFundraise.value).unit}
                            </div>
                          </div>
                        )}
                    </TableCell>
                    <TableCell
                      className="post-money-valuation"
                      width="104px"
                      css={css`
                        vertical-align: middle;
                      `}
                    >
                      <div
                        css={css`
                          display: flex;
                          justify-content: end;
                        `}
                      >
                        <div
                          className="currency"
                          css={css`
                            margin-right: 2px;
                          `}
                        >
                          {company.valuation && company.valuation.currency}
                        </div>
                        <div className="value">
                          {company.valuation?.value &&
                            toUnitNumber(company.valuation.value).value.toFixed(
                              1
                            )}
                        </div>
                        <div className="unit">
                          {company.valuation &&
                            toUnitNumber(company.valuation.value).unit}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
            </tbody>
          </Table>
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              padding-top: 8px;
              padding-bottom: 8px;
              background-color: ${colors.white10};
            `}
            className="overseas-company-pagination"
          >
            <Pagination
              limit={COMPANIES_PER_PAGE}
              count={count}
              page={page}
              setPage={handleChangePage}
            />
          </div>
        </>
      ) : (
        <EmptyCard />
      )}
    </Card>
  )
}
